/**
 * D-EZ-109 — THE WIRE, ON SCREEN: The Stock Car Dispatch's weekly news page.
 *
 * Between race N and race N+1 the season home prints the week's Wire under the
 * "Play the next race" button: the front-page line, what was said in the
 * garage, the power rankings, the paper's line on next week, the injury
 * report, the sponsor desk and the rivalry watch. Every back week of the season
 * is one tap away (‹ ›), and the newsstand keeps every season's Wires.
 *
 * ⚠ WRITTEN BY THE GAME, FOR FREE. Nothing here calls a model; the Wire is a
 * pure function of the league's history (`domain/wire`), printed again
 * whenever it is opened — nothing is stored. The only model call on this page
 * is behind the explicit "Read the race write-up" → "Have the writer do it"
 * action of The Stock Car Dispatch (Sept 12 ruling: never automatic).
 *
 * ⚠ THE ODDS ARE THE PAPER'S. The page says so beside them: the race is
 * decided on the dice and nothing here changes a roll.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { buildWire, wireLeague, wireWeeks, type Wire, type WireLeague } from '../domain/wire/index.ts';
import type { SettledSeasonLike, LiveSeasonLike } from '../domain/wire/source.ts';
import type { PowerRow } from '../domain/wire/power.ts';

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

function Move({ row }: { row: PowerRow }) {
  if (row.prevRank === null || row.move === null) {
    return (
      <span className="wpower__move wpower__move--new" aria-label="new to the top ten">
        new
      </span>
    );
  }
  if (row.move === 0) {
    return (
      <span className="wpower__move wpower__move--same" aria-label="no change">
        –
      </span>
    );
  }
  const up = row.move > 0;
  return (
    <span className={`wpower__move wpower__move--${up ? 'up' : 'down'}`} aria-label={`${up ? 'up' : 'down'} ${Math.abs(row.move)}`}>
      {up ? '▲' : '▼'}
      {Math.abs(row.move)}
    </span>
  );
}

export interface WirePaperProps {
  wire: Wire;
  /** The week stepper, drawn in the flag. */
  nav?: ReactNode;
  /** Opens race N's edition of The Stock Car Dispatch (the written one; a model only on its own explicit action). */
  onReadRace?: () => void;
  /** Heading level for the headline: 2 inline on a page, 1 in a dialog. */
  level?: 1 | 2;
}

export function WirePaper({ wire, nav, onReadRace, level = 2 }: WirePaperProps) {
  const H = level === 1 ? 'h1' : 'h2';
  const id = `wire-${wire.seasonNumber}-${wire.afterRace}`;
  const allReal = wire.quotes.length > 0 && wire.quotes.every((q) => q.real);
  return (
    <article className="wire" aria-labelledby={`${id}-h`}>
      <header className="wire__flag">
        <div className="wire__mast">
          <p className="wire__masthead">The Stock Car Dispatch</p>
          <p className="wire__title">The Wire</p>
        </div>
        <p className="wire__dateline">
          <span>
            Season {wire.seasonNumber} · the week after race {wire.afterRace}, {wire.raceName}
          </span>
          {wire.next && (
            <span>
              Next: {wire.next.raceName} at {wire.next.track} · {wire.next.turns} turns
              {wire.next.doublePoints ? ' · double points' : ''}
              {wire.next.round !== 'Regular Season' ? ` · ${wire.next.round}` : ''}
            </span>
          )}
        </p>
        {nav}
      </header>

      <H id={`${id}-h`} className="wire__headline">
        {wire.headline.text}
      </H>
      {wire.standfirst && <p className="wire__standfirst">{wire.standfirst.text}</p>}

      <div className="wire__cols">
        <div className="wire__col">
          {wire.quotes.length > 0 && (
            <section className="wire__sec" aria-labelledby={`${id}-garage`}>
              <h3 id={`${id}-garage`} className="wire__sechead">
                {allReal ? 'From the results' : 'Heard in the garage'}
              </h3>
              <ul className="wquotes">
                {wire.quotes.map((q) =>
                  q.quote ? (
                    <li key={q.driverId} className={`wquote wquote--${q.role}`}>
                      <blockquote className="wquote__words">
                        <p>“{q.quote}”</p>
                      </blockquote>
                      <p className="wquote__who">
                        <b>{q.name}</b> <span className="wquote__fact">{q.context.text}</span>
                      </p>
                    </li>
                  ) : (
                    <li key={q.driverId} className="wquote wquote--report">
                      <p className="wquote__report">{q.context.text}</p>
                    </li>
                  ),
                )}
              </ul>
            </section>
          )}

          {wire.rivalries.length > 0 && (
            <section className="wire__sec" aria-labelledby={`${id}-rivals`}>
              <h3 id={`${id}-rivals`} className="wire__sechead">
                Rivalry watch
              </h3>
              {wire.rivalries.map((r) => (
                <div key={`${r.a}|${r.b}`} className="wrival">
                  <p className="wrival__pair">
                    <b>
                      {r.aName} <span className="wrival__v">v</span> {r.bName}
                    </b>
                    <span className={`wrival__state wrival__state--${r.state}`}>{r.stateWord}</span>
                    {r.badge && <span className={`wrival__badge wrival__badge--${r.change}`}>{r.badge.text}</span>}
                  </p>
                  <ul className="wrival__lines">
                    {r.lines.map((l, i) => (
                      <li key={i}>{l.text}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </section>
          )}
        </div>

        <div className="wire__col">
          {wire.power.length > 0 && (
            <section className="wire__sec" aria-labelledby={`${id}-power`}>
              <h3 id={`${id}-power`} className="wire__sechead">
                Power rankings
              </h3>
              <ol className="wpower">
                {wire.power.map((p) => (
                  <li key={p.driverId} className="wpower__row">
                    <span className="wpower__rank">{p.rank}</span>
                    <Move row={p} />
                    <span className="wpower__body">
                      <span className="wpower__name">{p.name}</span>
                      <span className="wpower__why">{p.reason.text}</span>
                    </span>
                  </li>
                ))}
              </ol>
              <p className="wire__fine">Recent finishes weighted, plus points. The paper’s opinion — it changes nothing.</p>
            </section>
          )}

          {wire.odds && wire.odds.entries.length > 0 && (
            <section className="wire__sec" aria-labelledby={`${id}-odds`}>
              <h3 id={`${id}-odds`} className="wire__sechead">
                The Dispatch line{wire.next ? ` · ${wire.next.raceName}` : ''}
              </h3>
              <ul className="wodds">
                {wire.odds.entries.map((e) => (
                  <li key={e.driverId} className="wodds__row">
                    <span className="wodds__name">{e.name}</span>
                    <span className="wodds__line">{e.line}</span>
                    {e.note && <span className="wodds__note">{e.note.text}</span>}
                  </li>
                ))}
                {wire.odds.field && (
                  <li className="wodds__row wodds__row--field">
                    <span className="wodds__name">The field</span>
                    <span className="wodds__line">{wire.odds.field.line}</span>
                    <span className="wodds__note">Everyone else, together.</span>
                  </li>
                )}
              </ul>
              <p className="wire__fine">
                The paper’s odds, for fun — figured from form and track history. The race is decided on the dice; nothing
                here changes a roll.
              </p>
            </section>
          )}

          {wire.injuries.length > 0 && (
            <section className="wire__sec" aria-labelledby={`${id}-inj`}>
              <h3 id={`${id}-inj`} className="wire__sechead">
                Injury report
              </h3>
              <ul className="wire__list">
                {wire.injuries.map((l, i) => (
                  <li key={i} className={l.kind === 'inj-died' ? 'wire__item wire__item--mourn' : 'wire__item'}>
                    {l.text}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {wire.sponsors.length > 0 && (
            <section className="wire__sec" aria-labelledby={`${id}-spons`}>
              <h3 id={`${id}-spons`} className="wire__sechead">
                Sponsor desk
              </h3>
              <ul className="wire__list">
                {wire.sponsors.map((l, i) => (
                  <li key={i} className="wire__item">
                    {l.text}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>

      <footer className="wire__foot">
        <span className="wire__source">Written by the game from the league’s own books — no model was called.</span>
        {onReadRace && (
          <button type="button" className="btn btn--quiet wire__btn wire__read" onClick={onReadRace}>
            Read the race write-up
          </button>
        )}
      </footer>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Paging through a season's Wires
// ---------------------------------------------------------------------------

function useWireWeeks(league: WireLeague, seasonNumber: number, start: number | null) {
  const weeks = useMemo(() => wireWeeks(league, seasonNumber), [league, seasonNumber]);
  const latest = weeks[weeks.length - 1] ?? null;
  const [week, setWeek] = useState<number | null>(start ?? latest);
  // A new race in the books moves the page to the newest Wire.
  const seen = useRef(latest);
  useEffect(() => {
    if (seen.current !== latest) {
      seen.current = latest;
      setWeek(latest);
    }
  }, [latest]);
  const shown = week !== null && weeks.includes(week) ? week : latest;
  const wire = useMemo(() => {
    if (shown === null) return null;
    try {
      return buildWire(league, seasonNumber, shown);
    } catch {
      return null;
    }
  }, [league, seasonNumber, shown]);
  const at = shown === null ? -1 : weeks.indexOf(shown);
  return {
    wire,
    week: shown,
    prev: at > 0 ? weeks[at - 1] : null,
    next: at >= 0 && at < weeks.length - 1 ? weeks[at + 1] : null,
    latest,
    setWeek,
  };
}

function WeekNav({ week, prev, next, latest, onGo }: { week: number; prev: number | null; next: number | null; latest: number | null; onGo: (n: number) => void }) {
  return (
    <nav className="wire__nav" aria-label="Earlier and later Wires">
      <button type="button" className="btn btn--quiet wire__btn" onClick={() => prev !== null && onGo(prev)} disabled={prev === null} aria-label="The week before">
        ‹ Earlier
      </button>
      <span className="wire__navnow" aria-live="polite">
        After race {week}
      </span>
      <button type="button" className="btn btn--quiet wire__btn" onClick={() => next !== null && onGo(next)} disabled={next === null} aria-label="The week after">
        Later ›
      </button>
      {latest !== null && week !== latest && (
        <button type="button" className="btn btn--quiet wire__btn" onClick={() => onGo(latest)}>
          Newest
        </button>
      )}
    </nav>
  );
}

// ---------------------------------------------------------------------------
// On the season home, between races
// ---------------------------------------------------------------------------

const FOLD_KEY = 'rwbr.wire.folded.v1';

function readFolded(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem(FOLD_KEY) === '1';
  } catch {
    return false;
  }
}

function writeFolded(on: boolean): void {
  try {
    window.localStorage?.setItem(FOLD_KEY, on ? '1' : '0');
  } catch {
    /* a private window keeps it open; nothing is lost */
  }
}

export interface SeasonWireProps {
  live: LiveSeasonLike;
  history?: readonly SettledSeasonLike[];
  career?: SettledSeasonLike | null;
  /** Opens race N's Monday edition of the paper. */
  onReadRace?: (seasonNumber: number, raceNumber: number) => void;
}

/** The newest Wire of the live season, with its back weeks, foldable. */
export function SeasonWire({ live, history = [], career = null, onReadRace }: SeasonWireProps) {
  const league = useMemo(
    () => wireLeague({ history: history.filter((h) => h.seasonNumber !== live.seasonNumber), career: career && career.seasonNumber !== live.seasonNumber ? career : null, live }),
    [history, career, live],
  );
  const { wire, week, prev, next, latest, setWeek } = useWireWeeks(league, live.seasonNumber, null);
  const [folded, setFolded] = useState(readFolded);
  const fold = useCallback((on: boolean) => {
    setFolded(on);
    writeFolded(on);
  }, []);
  if (!wire || week === null) return null;
  if (folded) {
    return (
      <section className="wirefold" aria-label="The Wire">
        <p className="wirefold__line">
          <span className="wirefold__mast">The Wire</span> {wire.headline.text}
        </p>
        <button type="button" className="btn btn--quiet wire__btn" onClick={() => fold(false)} aria-expanded={false}>
          Read this week’s Wire
        </button>
      </section>
    );
  }
  return (
    <div className="wirebox">
      <WirePaper
        wire={wire}
        nav={
          <div className="wire__tools">
            <WeekNav week={week} prev={prev} next={next} latest={latest} onGo={setWeek} />
            <button type="button" className="btn btn--quiet wire__btn" onClick={() => fold(true)} aria-expanded>
              Fold the Wire
            </button>
          </div>
        }
        onReadRace={onReadRace ? () => onReadRace(wire.seasonNumber, wire.afterRace) : undefined}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// From the newsstand: any season's Wires, in the paper's dialog
// ---------------------------------------------------------------------------

export interface WireModalProps {
  shelf: { history: readonly SettledSeasonLike[]; career?: SettledSeasonLike | null; live?: LiveSeasonLike | null };
  seasonNumber: number;
  onClose: () => void;
  onReadRace?: (seasonNumber: number, raceNumber: number) => void;
}

export function WireModal({ shelf, seasonNumber, onClose, onReadRace }: WireModalProps) {
  const league = useMemo(() => wireLeague(shelf), [shelf]);
  const { wire, week, prev, next, latest, setWeek } = useWireWeeks(league, seasonNumber, null);
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    panel.current?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="paper__scrim" role="presentation" onClick={onClose}>
      <div className="paper wire__dialog" role="dialog" aria-modal="true" aria-label="The Wire" tabIndex={-1} ref={panel} onClick={(e) => e.stopPropagation()}>
        <button type="button" className="paper__x" onClick={onClose} aria-label="Close the Wire">
          ×
        </button>
        {wire && week !== null ? (
          <WirePaper
            wire={wire}
            level={1}
            nav={<WeekNav week={week} prev={prev} next={next} latest={latest} onGo={setWeek} />}
            onReadRace={onReadRace ? () => onReadRace(wire.seasonNumber, wire.afterRace) : undefined}
          />
        ) : (
          <p className="wire__empty">No Wire for this season yet — the first one prints after race 1.</p>
        )}
      </div>
    </div>
  );
}
